import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { kinds } from '@/app/site/scope';
import { CatalogGroup, CatalogHead } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { Sketch } from '@/features/catalog/Sketch';
import { sketchOf } from '@/features/catalog/sketchOf';
import { BUILTIN_PREFIX, useBlockTypes } from '@/features/catalog/useBlockTypes';
import { useSchemaTypes } from '@/features/catalog/useSchemaTypes';
import { contentTypesGet, contentTypesUsage } from '@/shared/api/generated/content/content';
import { pluralize } from '@/shared/lib/plural';
import { Button } from '@/shared/ui/Button';

import { RECORDS } from '../SchemaPage/kinds';
import { RemoveTypeDialog } from '../SchemaPage/RemoveTypeDialog';
import { TypeFields } from '../SchemaPage/TypeFields';
import { TypeSettingsForm } from '../SchemaPage/TypeSettingsForm';
import styles from './ComponentPage.module.scss';

/**
 * Деталка компонента: эскиз, где стоит, поля и настройки. Встроенный —
 * только для чтения: его поля заданы Faceta.
 */
export function ComponentPage() {
  const { siteId, env, typeId } = useParams({
    from: '/authed/sites/$siteId/$env/components/$typeId',
  });

  return typeId.startsWith(BUILTIN_PREFIX) ? (
    <BuiltinComponent slug={typeId.slice(BUILTIN_PREFIX.length)} />
  ) : (
    <OwnComponent key={typeId} siteId={siteId} env={env} typeId={typeId} />
  );
}

function OwnComponent({
  siteId,
  env,
  typeId,
}: {
  siteId: string;
  env: 'prod' | 'dev';
  typeId: string;
}) {
  const navigate = useNavigate();
  const [removing, setRemoving] = useState(false);
  const blocks = useBlockTypes(siteId, env);
  const types = useSchemaTypes(siteId, env);

  const details = useQuery({
    queryKey: ['content-type', typeId],
    queryFn: async () => {
      const response = await contentTypesGet(typeId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос компонента');
      }

      return response.data;
    },
  });

  const usage = useQuery({
    queryKey: ['content-type', typeId, 'usage'],
    queryFn: async () => {
      const response = await contentTypesUsage(typeId);

      return response.status === 200 ? response.data : undefined;
    },
  });

  const type = details.data?.type;

  if (type === undefined) {
    return details.isError ? (
      <p className={catalog['empty']}>Такого компонента нет — возможно, его удалили.</p>
    ) : null;
  }

  const fields = blocks.data?.find((item) => item.type === type.slug)?.fields ?? [];
  const places = usage.data?.used_in ?? [];
  const groups = [
    ...new Set(
      (types.data ?? [])
        .filter((item) => item.kind === 'block' && item.folder !== '')
        .map((item) => item.folder),
    ),
  ];

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title={type.name}
        subtitle={
          <>
            <span className={catalog['mono']}>{type.slug}</span>
            {type.folder !== '' && ` · ${type.folder}`}
            {type.description !== '' && ` · ${type.description}`}
          </>
        }
      />

      <div className={styles['top']}>
        <Sketch kind={sketchOf(type.slug, fields)} className={styles['sketch']} />
        <div className={styles['usage']}>
          <h2 className={styles['label']}>Где стоит</h2>
          {places.length === 0 ? (
            <p className={catalog['empty']}>Пока ни на одной странице.</p>
          ) : (
            <ul className={styles['places']}>
              {places.map((place) => (
                <li key={`${place.type_id}:${place.field_key}`}>
                  {place.type_name} → {place.field_name}: {pluralize(place.records, RECORDS)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <CatalogGroup title="Поля">
        <TypeFields type={type} />
      </CatalogGroup>

      <CatalogGroup title="Настройки">
        <TypeSettingsForm
          key={type.etag}
          siteID={siteId}
          environment={kinds[env]}
          type={type}
          section="component"
          groups={groups}
          onSaved={() => {
            void details.refetch();
          }}
        />
        <div>
          <Button
            variant="ghost"
            tone="danger"
            iconLeft={<Trash2 size={15} />}
            onClick={() => {
              setRemoving(true);
            }}
          >
            Убрать компонент в корзину
          </Button>
        </div>
      </CatalogGroup>

      <RemoveTypeDialog
        type={removing ? type : undefined}
        onClose={() => {
          setRemoving(false);
        }}
        onRemoved={() => {
          void navigate({ to: '/sites/$siteId/$env/components', params: { siteId, env } });
        }}
      />
    </div>
  );
}

function BuiltinComponent({ slug }: { slug: string }) {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env' });
  const blocks = useBlockTypes(siteId, env);
  const item = blocks.data?.find((block) => block.type === slug && block.type_id === undefined);

  if (item === undefined) {
    return blocks.isPending ? null : <p className={catalog['empty']}>Такого компонента нет.</p>;
  }

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title={item.title}
        subtitle={
          <>
            <span className={catalog['mono']}>{item.type}</span> · встроенный компонент Faceta
            {item.hint !== '' && ` · ${item.hint}`}
          </>
        }
      />
      <div className={styles['top']}>
        <Sketch kind={sketchOf(item.type, [])} className={styles['sketch']} />
      </div>
      <CatalogGroup title="Поля">
        <ul className={styles['fields']}>
          {(item.fields ?? []).map((field) => (
            <li key={field.key}>
              <b>{field.name}</b>
              <span className={catalog['mono']}>{field.key}</span>
              <span className={styles['type']}>{field.type}</span>
            </li>
          ))}
        </ul>
      </CatalogGroup>
    </div>
  );
}
